import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface ScheduleItem {
  id: number
  title: string | null
  start_date: string | null
  end_date: string | null
  no_start: boolean
  no_end: boolean
  leader_name: string | null
  contact_name: string | null
  notes: string | null
  team: string | null
  date_description: string | null
}

interface Props {
  customer: Customer
  schedules: ScheduleItem[]
  can_add: boolean
}

export default function Index({ customer, schedules, can_add }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Schedule", href: `/customers/${customer.id}/schedules` },
  ]

  const basePath = `/customers/${customer.id}/schedules`

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this meeting?")) {
      router.delete(`${basePath}/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Schedule - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Scheduled Meetings for {customer.display_name}
          </h1>
          {can_add && (
            <Link
              href={`${basePath}/new`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Schedule Meeting
            </Link>
          )}
        </div>

        {schedules.length > 0 ? (
          <div className="space-y-3">
            {schedules.map((schedule) => (
              <Link
                key={schedule.id}
                href={`${basePath}/${schedule.id}`}
                className="border-hpi-light-gray hover:bg-hpi-pale-blue block rounded border p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3">
                      <span className="text-hpi-dark-gray font-semibold whitespace-nowrap">
                        {schedule.date_description}
                      </span>
                      {schedule.title && (
                        <span className="font-medium">{schedule.title}</span>
                      )}
                    </div>
                    {schedule.contact_name && (
                      <div className="text-hpi-med-gray mt-1 text-sm">
                        Contact: {schedule.contact_name}
                      </div>
                    )}
                    {schedule.notes && (
                      <div className="text-hpi-med-gray mt-1 max-w-2xl truncate text-sm">
                        {schedule.notes}
                      </div>
                    )}
                    {(schedule.leader_name ?? schedule.team) && (
                      <div className="text-hpi-med-gray mt-1 text-sm">
                        {schedule.leader_name && (
                          <span className="font-semibold">
                            {schedule.leader_name}
                          </span>
                        )}
                        {schedule.team && (
                          <span>
                            {schedule.leader_name ? " / " : ""}
                            {schedule.team}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {can_add && (
                    <div
                      className="flex gap-2 whitespace-nowrap"
                      onClick={(e) => e.preventDefault()}
                    >
                      <Link
                        href={`${basePath}/${schedule.id}/edit`}
                        className="text-hpi-active-blue text-sm hover:underline"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(schedule.id)}
                        className="text-hpi-red text-sm hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center">
            <p className="text-hpi-med-gray mb-4">
              No scheduled meetings found for this customer.
            </p>
            {can_add && (
              <Link
                href={`${basePath}/new`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue inline-block rounded px-4 py-2 text-white"
              >
                Schedule Meeting
              </Link>
            )}
          </div>
        )}

        <div className="mt-4">
          <Link
            href={`/customers/${customer.id}`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Customer
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
